/**
 * Conexión de Redes Sociales (Facebook / Instagram) — LeadCEM.
 *
 * Rediseño "increíble" + embedded signup 1-clic. El tenant da un clic, autoriza en
 * la ventana OFICIAL de Meta y elige su página; NO pega tokens ni crea apps.
 * A partir de ahí: Messenger + Instagram DM + comentarios + Formularios (Lead Ads)
 * entran a "Conversaciones IA" y la IA contesta.
 *
 * El pegado manual de token queda como "Conexión avanzada" (colapsado, para casos raros).
 * Endpoints embebidos: /integrations/meta/leadforms/connect/start | /connect/status | /select-page
 * (server-side OAuth, token cifrado). Manual: /messenger/connect. Estado: /messenger/connection + /messenger/setup.
 */
import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import HelpTip from '@/components/HelpTip';
import {
  Loader2, Facebook, Instagram, MessageCircle, ClipboardList, MessagesSquare,
  ShieldCheck, CheckCircle2, ExternalLink, ChevronDown, Copy, Sparkles, Link2Off, Music2, Linkedin, Share2,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

const EMPTY_FORM = { page_id: '', page_name: '', page_access_token: '' };

// Canales que se encienden al conectar. Se muestran como preview y como estado.
const CANALES = [
  { key: 'messenger', icon: MessageCircle, label: 'Messenger', desc: 'Mensajes de tu página de Facebook' },
  { key: 'instagram', icon: Instagram, label: 'Instagram DM', desc: 'Mensajes directos de Instagram' },
  { key: 'comentarios', icon: MessagesSquare, label: 'Comentarios', desc: 'Respuestas a comentarios en tus publicaciones' },
  { key: 'formularios', icon: ClipboardList, label: 'Formularios', desc: 'Leads de anuncios (Lead Ads) directo al CRM' },
];

function CopyRow({ label, value, testid }) {
  const copy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(
      () => toast.success('Copiado'),
      () => toast.error('No se pudo copiar'),
    );
  };
  return (
    <div className="space-y-1">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="flex gap-2 items-center">
        <code className="flex-1 text-[11px] text-sky-300 break-all bg-black/40 rounded px-2 py-1.5" data-testid={testid}>
          {value || '…'}
        </code>
        <Button type="button" size="sm" variant="outline" className="text-xs border-white/10 shrink-0" onClick={copy} disabled={!value}>
          <Copy className="w-3 h-3 mr-1" /> Copiar
        </Button>
      </div>
    </div>
  );
}

function CanalChip({ icon: Icon, label, desc, on }) {
  return (
    <div className={`flex items-start gap-2.5 rounded-xl border p-3 transition-colors ${on ? 'border-[#b6f24a]/30 bg-[#b6f24a]/[0.06]' : 'border-white/10 bg-black/20'}`}>
      <div className={`shrink-0 rounded-lg p-1.5 ${on ? 'bg-[#b6f24a]/15 text-[#b6f24a]' : 'bg-white/5 text-sky-300'}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-white flex items-center gap-1.5">
          {label}
          {on && <CheckCircle2 className="w-3 h-3 text-[#b6f24a]" />}
        </p>
        <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{desc}</p>
      </div>
    </div>
  );
}

function MetaFacebookInstagramPanel() {
  const [loading, setLoading] = useState(true);
  const [connection, setConnection] = useState(null);
  const [setup, setSetup] = useState(null);

  // Flujo embebido (1-clic)
  const [flow, setFlow] = useState('idle'); // idle|starting|waiting|pick|selecting|error
  const [state, setState] = useState('');
  const [link, setLink] = useState('');
  const [pages, setPages] = useState([]);
  const pollRef = useRef(null);

  // Manual (avanzado)
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/messenger/connection').catch((e) => {
        if (e?.response?.status === 404) return { data: null };
        throw e;
      }),
      api.get('/messenger/setup').catch(() => ({ data: null })),
    ])
      .then(([c, s]) => { setConnection(c.data); setSetup(s.data); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'No se pudo cargar la conexión'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ---- Embedded 1-clic ----
  const startEmbedded = async () => {
    setFlow('starting');
    try {
      const { data } = await api.post('/integrations/meta/leadforms/connect/start', {});
      setState(data.state);
      setLink(data.link);
      setFlow('waiting');
      const win = window.open(data.link, '_blank', 'noopener,width=680,height=760');
      if (!win) toast.info('Abre la ventana de conexión con el enlace de abajo.');
      pollRef.current = setInterval(() => poll(data.state), 3500);
    } catch (e) {
      setFlow('error');
      toast.error(e?.response?.data?.detail || 'No se pudo iniciar la conexión. ¿Falta configurar la App de Meta?');
    }
  };

  const poll = async (st) => {
    try {
      const { data } = await api.get('/integrations/meta/leadforms/connect/status', { params: { state: st } });
      if (data.status === 'connected_pick') {
        clearInterval(pollRef.current);
        setPages(data.pages || []);
        setFlow('pick');
      } else if (data.status === 'connected') {
        clearInterval(pollRef.current);
        toast.success('¡Página conectada!');
        setFlow('idle');
        load();
      } else if (data.status === 'no_pages') {
        clearInterval(pollRef.current);
        setFlow('error');
        toast.error('No encontramos páginas que administres con esa cuenta de Meta.');
      } else if (data.status === 'error' || data.status === 'cancelled') {
        clearInterval(pollRef.current);
        setFlow('error');
        toast.error('La conexión no se completó. Intenta de nuevo.');
      }
    } catch { /* sigue esperando */ }
  };

  const pickPage = async (pageId) => {
    setFlow('selecting');
    try {
      const { data } = await api.post('/integrations/meta/leadforms/select-page', { state, page_id: pageId });
      if (data.leadgen_subscribed) toast.success('¡Conectado! Ya entra todo a Conversaciones IA.');
      else toast.warning('Página conectada. Formularios quedó pendiente: ' + (data.subscribe_error || 'permiso insuficiente'));
      setFlow('idle');
      load();
    } catch (e) {
      if (e.response?.status === 409) {
        setFlow('error');
        toast.error('Ya tienes una página conectada. Desconéctala primero abajo y vuelve a intentar.');
      } else {
        setFlow('pick');
        toast.error(e.response?.data?.detail || 'No se pudo seleccionar la página');
      }
    }
  };

  // ---- Manual (avanzado) ----
  const connectManual = async () => {
    if (!form.page_id.trim() || !form.page_name.trim() || !form.page_access_token.trim()) {
      toast.error('Faltan el ID, el nombre o la clave de tu página'); return;
    }
    if (!/^\d{5,}$/.test(form.page_id.trim())) {
      toast.error('El ID de la página son solo números (mínimo 5 dígitos).'); return;
    }
    setConnecting(true);
    try {
      await api.post('/messenger/connect', form);
      toast.success('Página conectada.');
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo conectar la página');
    } finally { setConnecting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('¿Quitar esta página? Dejarán de entrar mensajes y leads de Facebook/Instagram. WhatsApp no se toca.')) return;
    setDisconnecting(true);
    try {
      await api.delete('/messenger/connection');
      toast.success('Página desconectada');
      setConnection(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo desconectar');
    } finally { setDisconnecting(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="messenger-loading">
        <Loader2 className="w-6 h-6 text-sky-300 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="messenger-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-white flex items-center gap-2">
          <span className="inline-flex -space-x-1.5">
            <Facebook className="w-6 h-6 text-sky-400" />
            <Instagram className="w-6 h-6 text-pink-400" />
          </span>
          Facebook e Instagram
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-xl leading-relaxed">
          Conecta tu página en <b className="text-white/90">1 clic</b> y deja que la IA atienda tus mensajes,
          comentarios y formularios — todo dentro de <b className="text-[#b6f24a]">Conversaciones IA</b>.
        </p>
      </div>

      {/* ESTADO CONECTADO */}
      {connection ? (
        <>
          <Card className="border-[#b6f24a]/25 bg-gradient-to-br from-[#b6f24a]/[0.07] to-transparent" data-testid="messenger-connected-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base flex items-center gap-2 flex-wrap">
                <CheckCircle2 className="w-5 h-5 text-[#b6f24a] shrink-0" />
                {connection.page_name}
                <Badge variant="outline" className="text-[9px] text-[#b6f24a] border-[#b6f24a]/30">Conectada</Badge>
              </CardTitle>
              <CardDescription className="text-xs">
                Todo lo que llegue a esta página entra a Conversaciones IA y la IA responde. WhatsApp sigue igual.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {CANALES.map((c) => <CanalChip key={c.key} {...c} on />)}
              </div>
              <div className="flex items-center justify-between pt-1">
                <p className="text-[11px] text-muted-foreground">ID de página: <span className="text-white/80">{connection.page_id}</span></p>
                <Button
                  size="sm" variant="outline" onClick={disconnect} disabled={disconnecting}
                  className="gap-1.5 text-rose-400 border-rose-500/30 hover:text-rose-300"
                  data-testid="messenger-disconnect-btn"
                >
                  {disconnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2Off className="w-3.5 h-3.5" />}
                  Quitar página
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          {/* HERO — Conectar 1 clic */}
          <Card className="relative overflow-hidden border-sky-500/20 bg-gradient-to-br from-sky-950/50 via-zinc-950/60 to-zinc-950/80" data-testid="meta-embedded-connect">
            <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-sky-500/10 blur-3xl" />
            <CardHeader className="pb-3 relative">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#b6f24a]" /> Conexión rápida (recomendada)
                <HelpTip title="Conexión segura y directa" content={'Se abre una ventana OFICIAL de Meta donde inicias sesión con TU cuenta y eliges tu página. No compartes contraseña ni token; LeadCEM recibe el permiso por detrás de forma cifrada.'} />
              </CardTitle>
              <CardDescription className="text-xs">
                1 clic, autorizas en una ventana oficial de Meta y listo. No creas ninguna app ni pegas claves.
              </CardDescription>
            </CardHeader>
            <CardContent className="relative space-y-4">
              {/* Preview de canales */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {CANALES.map((c) => <CanalChip key={c.key} {...c} on={false} />)}
              </div>

              {flow === 'idle' && (
                <Button onClick={startEmbedded} className="w-full sm:w-auto gap-2 bg-sky-600 hover:bg-sky-500" data-testid="meta-connect-btn">
                  <Facebook className="w-4 h-4" /> Conectar con Facebook / Instagram
                </Button>
              )}
              {flow === 'starting' && (
                <div className="flex items-center gap-2 text-sm text-zinc-300"><Loader2 className="w-4 h-4 animate-spin" /> Preparando tu conexión segura…</div>
              )}
              {flow === 'waiting' && (
                <div className="space-y-2" data-testid="meta-waiting">
                  <div className="flex items-center gap-2 text-sm text-sky-300"><Loader2 className="w-4 h-4 animate-spin" /> Esperando que autorices en la ventana de Meta…</div>
                  <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-sky-300 underline break-all">
                    <ExternalLink className="w-3.5 h-3.5" /> ¿No se abrió? Ábrela aquí
                  </a>
                </div>
              )}
              {flow === 'pick' && (
                <div className="space-y-2" data-testid="meta-pick">
                  <p className="text-sm text-zinc-200">Elige la página que quieres conectar:</p>
                  <div className="space-y-1.5">
                    {pages.map((p) => (
                      <Button key={p.id} variant="outline" className="w-full justify-start border-white/10 hover:border-sky-500/40"
                        onClick={() => pickPage(p.id)} data-testid={`meta-page-${p.id}`}>
                        <Facebook className="w-3.5 h-3.5 mr-2 text-sky-400" /> {p.name || p.id}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              {flow === 'selecting' && (
                <div className="flex items-center gap-2 text-sm text-sky-300"><Loader2 className="w-4 h-4 animate-spin" /> Conectando la página y activando canales…</div>
              )}
              {flow === 'error' && (
                <div className="space-y-2">
                  <p className="text-sm text-amber-300">No se pudo completar la conexión.</p>
                  <Button onClick={startEmbedded} variant="outline" size="sm">Reintentar</Button>
                </div>
              )}

              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-1">
                <ShieldCheck className="w-3.5 h-3.5 text-[#b6f24a]" />
                Tu contraseña y tu token nunca pasan por aquí. La conexión la guarda LeadCEM cifrada.
              </div>
            </CardContent>
          </Card>

          {/* Conexión avanzada (manual) — colapsada */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-white transition-colors"
              data-testid="meta-advanced-toggle"
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
              Conexión avanzada (manual, con token)
            </button>

            {showAdvanced && (
              <Card className="bg-card/50 backdrop-blur-md border-white/5 mt-3" data-testid="messenger-manual">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-sm">Conectar pegando el token de la página</CardTitle>
                  <CardDescription className="text-xs">Solo si no puedes usar la conexión rápida. La clave se guarda cifrada.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      ID de tu página (solo números)
                      <HelpTip title="ID de la página" content={'Es un número, no el nombre.\n\nBien: 102938475610\nMal: GMC Cuautla\n\nFacebook → tu página → Información → Identificador de la página.'} />
                    </Label>
                    <Input value={form.page_id}
                      onChange={(e) => setForm({ ...form, page_id: e.target.value.replace(/[^\d]/g, '') })}
                      placeholder="Ej. 102938475610" inputMode="numeric"
                      className="bg-black/20 border-white/10 text-xs h-9" data-testid="messenger-page-id" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Nombre de tu página</Label>
                    <Input value={form.page_name}
                      onChange={(e) => setForm({ ...form, page_name: e.target.value })}
                      placeholder="Ej. GMC Cuautla"
                      className="bg-black/20 border-white/10 text-xs h-9" data-testid="messenger-page-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      Clave de conexión (token)
                      <HelpTip title="Clave de conexión" content={'No es la contraseña de Facebook. Es una clave larga que empieza por EAA… Si la pegas mal, no llegan mensajes.'} />
                    </Label>
                    <Input type="password" value={form.page_access_token}
                      onChange={(e) => setForm({ ...form, page_access_token: e.target.value })}
                      placeholder="Pégala aquí. No la escribas a mano." autoComplete="off"
                      className="bg-black/20 border-white/10 text-xs h-9" data-testid="messenger-page-token" />
                  </div>
                  <Button size="sm" onClick={connectManual} disabled={connecting} className="gap-1.5" data-testid="messenger-connect-btn">
                    {connecting && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Conectar mi página
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Ayuda webhook (solo si el backend lo pide en setup) */}
          {setup?.webhook_url && (
            <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="messenger-meta-ops">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-sm">Si Facebook te pide una dirección (webhook)</CardTitle>
                <CardDescription className="text-xs">Esto lo configura LeadCEM una sola vez. Si te sale la pantalla de «Callback URL», copia y pega.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <CopyRow label="Dirección (Callback URL)" value={setup.webhook_url} testid="messenger-webhook-url" />
                <CopyRow label="Token de verificación" value={setup.verify_token} testid="messenger-verify-token" />
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}


// ============ Pestaña TikTok (connect real, contrato sesion TikTok) ============
const TIKTOK_CANALES = [
  { icon: MessageCircle, label: 'Mensajes directos', desc: 'DMs de tu cuenta de TikTok' },
  { icon: MessagesSquare, label: 'Comentarios', desc: 'Respuestas a comentarios de tus videos' },
  { icon: ClipboardList, label: 'Leads', desc: 'Leads de tus anuncios directo al CRM' },
];

function TikTokPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const pollRef = useRef(null);

  const loadStatus = async () => {
    try {
      const { data } = await api.get('/integrations/tiktok/status');
      setStatus(data || {});
    } catch {
      setStatus({ platform_ready: false });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const startConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post('/integrations/tiktok/connect/start');
      if (!data || !data.link || !data.state) { toast.error('No se pudo iniciar la conexion'); setConnecting(false); return; }
      window.open(data.link, 'tiktok_oauth', 'width=600,height=760');
      const state = data.state;
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get('/integrations/tiktok/connect/status?state=' + encodeURIComponent(state));
          const st = r && r.data && r.data.status;
          if (st === 'connected') { clearInterval(pollRef.current); setConnecting(false); toast.success('TikTok conectado'); loadStatus(); }
          else if (st === 'error' || st === 'cancelled') { clearInterval(pollRef.current); setConnecting(false); toast.error('No se completo la conexion con TikTok'); }
        } catch (e) { /* sigue intentando */ }
      }, 2500);
    } catch (e) {
      toast.error('No se pudo iniciar la conexion con TikTok');
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    try { await api.delete('/integrations/tiktok/disconnect'); toast.success('TikTok desconectado'); loadStatus(); }
    catch (e) { toast.error('No se pudo desconectar'); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  if (!status || !status.platform_ready) {
    return (
      <Card className="border-white/10 bg-black/20">
        <CardContent className="py-10 text-center space-y-2">
          <Music2 className="w-8 h-8 mx-auto text-fuchsia-400/60" />
          <p className="text-sm font-medium text-white">TikTok aun no disponible</p>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">Muy pronto podras conectar TikTok en 1 clic, igual que Facebook.</p>
        </CardContent>
      </Card>
    );
  }

  if (status.connected) {
    return (
      <Card className="border-[#b6f24a]/30 bg-[#b6f24a]/[0.06]">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2 text-white"><CheckCircle2 className="w-5 h-5 text-[#b6f24a]" /> TikTok conectado</CardTitle>
          <CardDescription>Conectado como <b className="text-white">{status.account || 'tu cuenta'}</b>. Tus mensajes y comentarios llegan a Conversaciones IA.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" size="sm" className="border-white/10 text-xs" onClick={disconnect} data-testid="tiktok-disconnect-btn"><Link2Off className="w-3 h-3 mr-1" /> Desconectar</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-white/10 bg-black/20">
      <CardHeader>
        <CardTitle className="text-base text-white flex items-center gap-2"><Music2 className="w-5 h-5 text-fuchsia-400" /> Conecta tu cuenta de TikTok</CardTitle>
        <CardDescription>1 clic, autorizas en la ventana oficial de TikTok y listo. Tus mensajes y comentarios llegaran a Conversaciones IA y la IA responde.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-3 gap-2">
          {TIKTOK_CANALES.map((c) => <CanalChip key={c.label} icon={c.icon} label={c.label} desc={c.desc} on={false} />)}
        </div>
        <Button className="w-full" onClick={startConnect} disabled={connecting} data-testid="tiktok-connect-btn">
          {connecting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Conectando…</> : <><Music2 className="w-4 h-4 mr-2" /> Conectar con TikTok</>}
        </Button>
        <p className="text-[10px] text-muted-foreground flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Tu contrasena nunca pasa por aqui. La conexion la guarda LeadCEM cifrada.</p>
      </CardContent>
    </Card>
  );
}

// ============ Pestaña LinkedIn (placeholder) ============
function LinkedInPanel() {
  return (
    <Card className="border-white/10 bg-black/20">
      <CardContent className="py-12 text-center space-y-3">
        <Linkedin className="w-10 h-10 mx-auto text-blue-400/70" />
        <p className="text-base font-semibold text-white">LinkedIn — muy pronto</p>
        <p className="text-xs text-muted-foreground max-w-sm mx-auto leading-relaxed">Estamos preparando la conexion con LinkedIn. Cuando este lista, atenderas tus mensajes de LinkedIn desde aqui, igual que las demas redes.</p>
      </CardContent>
    </Card>
  );
}

// ============ Pagina Redes Sociales (default export, orquesta las 3 pestañas) ============
export default function SocialNetworksPage() {
  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-6" data-testid="social-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-white flex items-center gap-2">
          <Share2 className="w-6 h-6 text-[#b6f24a]" /> Redes Sociales
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-xl leading-relaxed">
          Conecta tus redes y deja que la IA atienda mensajes, comentarios y formularios — todo dentro de <b className="text-[#b6f24a]">Conversaciones IA</b>.
        </p>
      </div>
      <Tabs defaultValue="meta" className="w-full">
        <TabsList className="bg-black/30 border border-white/5 mb-4 flex w-full h-auto gap-1 p-1">
          <TabsTrigger value="meta" data-testid="tab-social-meta" className="flex-1 flex-col sm:flex-row gap-1 h-auto py-2 whitespace-normal leading-tight text-[11px] sm:text-sm data-[state=active]:bg-sky-500/20 data-[state=active]:text-sky-300">
            <span className="inline-flex -space-x-1"><Facebook className="w-4 h-4 text-sky-400" /><Instagram className="w-4 h-4 text-pink-400" /></span>
            Facebook e Instagram
          </TabsTrigger>
          <TabsTrigger value="tiktok" data-testid="tab-social-tiktok" className="flex-1 flex-col sm:flex-row gap-1 h-auto py-2 whitespace-normal leading-tight text-[11px] sm:text-sm data-[state=active]:bg-fuchsia-500/20 data-[state=active]:text-fuchsia-300">
            <Music2 className="w-4 h-4" /> TikTok
          </TabsTrigger>
          <TabsTrigger value="linkedin" data-testid="tab-social-linkedin" className="flex-1 flex-col sm:flex-row gap-1 h-auto py-2 whitespace-normal leading-tight text-[11px] sm:text-sm data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-300">
            <Linkedin className="w-4 h-4" /> LinkedIn
          </TabsTrigger>
        </TabsList>
        <TabsContent value="meta"><MetaFacebookInstagramPanel /></TabsContent>
        <TabsContent value="tiktok"><TikTokPanel /></TabsContent>
        <TabsContent value="linkedin"><LinkedInPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
